import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

/** sessionStorage key marking that we've already forced a reload this session. */
const RELOAD_FLAG = 'gg-chunk-reload';

/** True if a reload has already been attempted this session (loop guard). */
function hasReloaded(): boolean {
  try {
    return sessionStorage.getItem(RELOAD_FLAG) === '1';
  } catch {
    // sessionStorage blocked (privacy mode); treat as "not reloaded".
    return false;
  }
}

function markReloaded(reloaded: boolean): void {
  try {
    if (reloaded) sessionStorage.setItem(RELOAD_FLAG, '1');
    else sessionStorage.removeItem(RELOAD_FLAG);
  } catch {
    // sessionStorage blocked; the loop guard is simply unavailable. Worst case
    // is one extra reload — the app still functions.
  }
}

/**
 * Resolve a dynamic import, recovering once from a stale-chunk failure.
 *
 * After a deploy, an already-open tab holds an old `index.html` that references
 * old hashed chunk filenames; navigating to a not-yet-loaded route then fails
 * to fetch the (now-removed) chunk. We reload the page once to pull the fresh
 * `index.html` + chunk hashes. If the import still fails after that reload
 * (genuinely offline, or a real bug), we rethrow so the route's `errorElement`
 * can render instead of looping forever (#160 review).
 *
 * Note: when the PWA service worker is controlling the page it serves the
 * precached `index.html`, so the SW's own `autoUpdate` flow is the primary
 * recovery path; this reload is the best-effort fallback for non-SW loads and
 * the brief window before the SW activates a new version.
 */
export async function loadWithReload<T>(
  factory: () => Promise<{ default: T }>
): Promise<{ default: T }> {
  try {
    const loaded = await factory();
    markReloaded(false); // success — re-arm for a future deploy
    return loaded;
  } catch (error) {
    if (hasReloaded()) {
      throw error; // already retried once; surface to the route errorElement
    }
    markReloaded(true);
    window.location.reload();
    // Hold the render in the Suspense fallback until the reload navigates away.
    return new Promise<{ default: T }>(() => {});
  }
}

/** `React.lazy` with one-time reload recovery for stale dynamic-import chunks. */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>
): LazyExoticComponent<T> {
  return lazy(() => loadWithReload(factory));
}
